import React from "react";

function CollapseView({
  raw,
  onEdit,
  onView,
  onPartialReceive,
  onReceive,
  onDelete,
}) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handleDown = (e) => {
      if (!open) return;
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const DataCard = ({ icon, label, value, accent = "#83c14a" }) => (
    <div style={{
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: "10px",
      padding: "16px",
      position: "relative",
      overflow: "hidden",
      transition: "all 0.2s ease"
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}>
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "4px",
        height: "100%",
        background: accent
      }}></div>
      <div className="d-flex align-items-start gap-3">
        <div style={{
          width: "40px",
          height: "40px",
          borderRadius: "10px",
          background: `${accent}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0
        }}>
          <i className={icon} style={{ fontSize: "1.125rem", color: accent }}></i>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "0.75rem", color: "#6B7280", fontWeight: "600", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {label}
          </div>
          <div style={{ fontSize: "1rem", color: "#111827", fontWeight: "700", wordBreak: "break-word" }}>
            {value || "—"}
          </div>
        </div>
      </div>
    </div>
  );

  const createdDate = raw?.created
    ? new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(raw.created))
    : "—";

  const updatedDate = raw?.updated
    ? new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(raw.updated))
    : "—";

  return (
    <div style={{
      background: "#F9FAFB",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      padding: "20px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "20px",
        paddingBottom: "16px",
        borderBottom: "2px solid #E5E7EB"
      }}>
        <i className="bi bi-truck" style={{ fontSize: "1.25rem", color: "#83c14a" }}></i>
        <h6 style={{ margin: 0, fontSize: "1rem", fontWeight: "700", color: "#111827" }}>
          Receivable Item Details
        </h6>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <DataCard 
            icon="bi bi-receipt"
            label="DR #"
            value={raw?.["dr#"]}
            accent="#83c14a"
          />
        </div>

        <div className="col-md-4">
          <DataCard 
            icon="bi bi-tag-fill"
            label="DR Type"
            value={raw?.drType}
            accent="#3B82F6"
          />
        </div>

        <div className="col-md-4">
          <DataCard 
            icon="bi bi-calendar-plus"
            label="Date Created"
            value={createdDate}
            accent="#8B5CF6"
          />
        </div>

        <div className="col-md-4">
          <DataCard 
            icon="bi bi-person-plus"
            label="Created By"
            value={raw?.createdBy?.email}
            accent="#F59E0B"
          />
        </div>

        <div className="col-md-4">
          <DataCard 
            icon="bi bi-calendar-check"
            label="Date Updated"
            value={updatedDate}
            accent="#06B6D4"
          />
        </div>

        <div className="col-md-4">
          <DataCard 
            icon="bi bi-person-circle"
            label="Updated By"
            value={raw?.updatedBy?.email ?? "N/A"}
            accent="#EC4899"
          />
        </div>
      </div>

      <div className="d-flex gap-3 align-items-center">
        {raw?.status === "Draft" && (
          <button 
            className="btn text-white" 
            onClick={onEdit}
            style={{
              background: "linear-gradient(135deg, #83c14a 0%, #6fa83d 100%)",
              border: "none",
              padding: "10px 20px",
              fontWeight: "600",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(131, 193, 74, 0.3)",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 4px 12px rgba(131, 193, 74, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 2px 8px rgba(131, 193, 74, 0.3)";
            }}
          >
            <i className="bi bi-pencil-fill me-2"></i> Edit
          </button>
        )}
        {raw?.status === "Partially Receive" && (
          <button 
            className="btn text-white" 
            onClick={onEdit}
            style={{
              background: "linear-gradient(135deg, #83c14a 0%, #6fa83d 100%)",
              border: "none",
              padding: "10px 20px",
              fontWeight: "600",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(131, 193, 74, 0.3)",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 4px 12px rgba(131, 193, 74, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 2px 8px rgba(131, 193, 74, 0.3)";
            }}
          >
            Receive
          </button>
        )}

        <button 
          className="btn" 
          onClick={onView}
          style={{
            background: "#F3F4F6",
            color: "#374151",
            padding: "10px 20px",
            fontWeight: "600",
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => {
            e.target.style.background = "#E5E7EB";
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "#F3F4F6";
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
          }}
        >
          <i className="bi bi-eye-fill me-2"></i> View
        </button>

        {/* three-dots + popover */}
        <div className="position-relative">
          {/* <button
            ref={triggerRef}
            type="button"
            className="btn p-1"
            aria-label="More actions"
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={() => setOpen((v) => !v)}
          >
            <i
              className="bi bi-three-dots-vertical fw-bold"
              style={{ color: "black", fontSize: "20px" }}
            />
          </button> */}

          {open && (
            <div
              ref={menuRef}
              role="menu"
              className="shadow rounded p-2"
              style={{
                position: "absolute",
                top: "50%",
                left: "calc(100% + 8px)",
                transform: "translateY(-50%)",
                zIndex: 1050,
                width: 200,
                background: "#fff",
              }}
            >
              <button
                className="btn w-100 fw-semibold mb-2"
                style={{ background: "#FFCC33", color: "#000" }}
                onClick={() => {
                  setOpen(false);
                  onPartialReceive();
                }}
              >
                Partially Receive
              </button>

              {raw?.status === "Draft" && (
                <>
                  <button
                    className="btn w-100 text-white fw-semibold mb-2"
                    style={{ background: "#82c14a" }}
                    onClick={() => {
                      setOpen(false);
                      onReceive();
                    }}
                  >
                    Receive
                  </button>

                  <button
                    className="btn w-100 text-white fw-semibold"
                    style={{ background: "#E4573D" }}
                    onClick={() => {
                      setOpen(false);
                      onDelete();
                    }}
                  >
                    <i className="bi bi-trash me-2" />
                    Delete
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CollapseView;
